import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { fmtDate } from "@/constants/sports";
import { api } from "@/lib/api";

const Stat = ({ label, value, testId, warn }) => (
  <div data-testid={testId} className="bg-[#131927] p-4">
    <div className="eyebrow">{label}</div>
    <div className={`mt-1.5 font-data text-sm font-bold ${warn ? "text-amber-400" : "text-white"}`}>{value ?? "—"}</div>
  </div>
);

const when = (iso) => (iso ? `${fmtDate(iso)} ${new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Never");

export const ConnectionCard = ({ onSync, busy }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/fanflare/status").then((r) => setS(r.data)).catch(() => setS({ connected: false })); }, [busy]);

  const Icon = !s ? Loader2 : s.connected ? (s.expiring_soon ? AlertTriangle : CheckCircle2) : XCircle;
  const color = !s ? "text-slate-500 animate-spin" : s.connected ? (s.expiring_soon ? "text-amber-400" : "text-emerald-400") : "text-red-400";
  const used = s?.monthly_limit && s.monthly_remaining != null ? s.monthly_limit - s.monthly_remaining : null;

  return (
    <div data-testid="fanflare-connection-card" className="rounded-xl border border-slate-800 bg-[#131927] p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-start gap-4">
          <Icon className={`mt-1 h-6 w-6 ${color}`} />
          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase">FanFlare connection</h2>
            <p data-testid="fanflare-status-text" className="mt-1 max-w-xl text-sm text-slate-400">
              {!s ? "Checking connection" : s.connected
                ? `Connected as "${s.client_name}" (${s.environment}). Stats refresh automatically every Monday at 7:00 UTC.`
                : s.configured ? `Not connected: ${s.error || "FanFlare did not respond"}` : "Not connected. No FanFlare API key is set on the server."}
            </p>
            {s?.expiring_soon && (
              <p data-testid="fanflare-expiry-warning" className="mt-2 text-sm text-amber-400">
                The API key expires in {s.expires_in_days} days. Create a new key in FanFlare Serve before {fmtDate(s.expires_at)}.
              </p>
            )}
          </div>
        </div>
        <button data-testid="sync-fanflare-button" onClick={onSync} disabled={busy || !s?.connected}
          className="inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#E01F66] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {busy ? "Syncing" : "Sync now"}
        </button>
      </div>
      {s?.connected && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-800 bg-slate-800 md:grid-cols-4">
            <Stat label="Requests / sec" value={`${s.requests_per_second} (burst ${s.burst})`} testId="fanflare-rate-limit" />
            <Stat label="Last sync" value={when(s.last_sync_at)} testId="fanflare-last-sync" />
            <Stat label="Key expires" value={s.expires_at ? `${fmtDate(s.expires_at)} (${s.expires_in_days}d)` : "Never"} testId="fanflare-key-expiry" warn={s.expiring_soon} />
            <Stat label="Monthly usage" value={used != null ? `${used.toLocaleString()} / ${s.monthly_limit.toLocaleString()}` : "No monthly limit"} testId="fanflare-monthly-usage" />
          </div>
          {used != null && <Progress data-testid="fanflare-usage-bar" value={(used / s.monthly_limit) * 100} className="mt-3 h-1.5 bg-slate-800" />}
          <div className="mt-6">
            <div className="eyebrow mb-2">Leagues on this key</div>
            <div className="flex flex-wrap gap-2">
              {s.leagues.map((l) => (
                <span key={l.code} data-testid={`fanflare-league-${l.code}`}
                  className={`rounded-full border px-3 py-1 text-xs ${l.imported ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-500"}`}>
                  {l.name}{l.imported ? " · imported" : ""}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
